'use client';
import { Fragment as _Fragment, jsx as _jsx } from "react/jsx-runtime";
import { createElement, useEffect, useMemo, createContext, useContext } from "react";
import { WalletProvidersProvider } from "../../../context/walletProviders";
import { WalletModalProvider } from "../WalletModal";
import { isWalletProviderDescriptor } from "@layerswap/wallet-core/types";
const DynamicProviderWrapper = ({ providers, children, appName }) => {
    const createNestedProviders = (providers, currentChildren) => {
        if (providers.length === 0)
            return currentChildren;
        const [currentProvider, ...remainingProviders] = providers;
        if (!currentProvider.wrapper) {
            return createNestedProviders(remainingProviders, currentChildren);
        }
        const wrapperProps = { children: createNestedProviders(remainingProviders, currentChildren), appName };
        return createElement(currentProvider.wrapper, wrapperProps);
    };
    return _jsx(_Fragment, { children: createNestedProviders(providers, children) });
};
/**
 * WalletsProviders - Dynamically renders wallet provider wrappers and runs
 * one-shot `init` lifecycles for providers that opt out of a React wrapper.
 *
 * Each entry in the array may be:
 * - a `WalletProvider`/`WalletWrapper` instance (eager — applied immediately)
 * - a `WalletProviderDescriptor` (lazy — passed through, hydrated by
 *   `DescriptorHydrationBoundary` upstream when the real provider is needed).
 *
 * Descriptors are filtered out before any consumer that requires runtime
 * provider data (`init`, `wrapper`, `createConnection`). They appear in the
 * list context so connector enumeration can see the descriptor id.
 */
const WalletsProviders = ({ children, appName, walletProviders }) => {
    const realProviders = useMemo(() => walletProviders.filter((p) => !isWalletProviderDescriptor(p)), [walletProviders]);
    useEffect(() => {
        const disposers = [];
        for (const p of realProviders) {
            const init = p.init;
            if (typeof init === 'function') {
                const dispose = init({ appName });
                if (typeof dispose === 'function')
                    disposers.push(dispose);
            }
        }
        return () => disposers.forEach(d => { try {
            d();
        }
        catch { /* swallow */ } });
        // appName is stable per LayerswapProvider mount.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [realProviders]);
    return (_jsx(WalletProvidersListContext.Provider, { value: walletProviders, children: _jsx(WalletModalProvider, { children: _jsx(DynamicProviderWrapper, { providers: realProviders, appName: appName, children: _jsx(WalletProvidersProvider, { walletProviders: walletProviders, children: children }) }) }) }));
};
export default WalletsProviders;
export const WalletProvidersListContext = createContext([]);
export const useWalletProvidersList = () => useContext(WalletProvidersListContext);
